import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import { useConversation } from '../hooks/useConversations'
import { prefetchAdjacent } from '../lib/prefetch'
import { sendCommand } from '../lib/extension'
import { db, type Message } from '../lib/db'
import ComposeBar from './ComposeBar'
import ReminderPicker from './ReminderPicker'
import TagBadge from './TagBadge'
import TagPicker from './TagPicker'
import CrmMatchPicker from './CrmMatchPicker'
import type { Tag } from '../hooks/useTags'
import { ActionTooltip, Kbd } from './Kbd'
import { formatConversationText } from '../lib/formatConversation'
import { enrichAndSync } from '../lib/enrichContact'
import { useCrmConnections } from '../hooks/useCrmConnections'
import { useCrmMapping, useActiveCrm } from '../hooks/useCrmMapping'
import type { CrmMapping } from '../lib/db'
import { fetchHarmonicMatchData, type CrmName } from '../lib/crm'
import { useToast } from '../hooks/useToast'
import { voidAsync } from '../lib/voidAsync'
import { safeExternalUrl } from '../lib/safeUrl'

interface ThreadViewProps {
  conversationUrn: string
  conversations: { conversationUrn: string }[]
  onMessageSent?: (conversationUrn: string) => void
  onArchive?: (conversationUrn: string) => void
  onRemind?: (conversationUrn: string, remindAt: Date) => void
  onClearReminder?: (conversationUrn: string) => void
  isReminded?: boolean
  allTags?: Tag[]
  getTagsForConversation?: (conversationUrn: string) => string[]
  // These come from useTagAssignments and are `mutateAsync`-backed —
  // they resolve when the insert/delete lands. Typed as Promise so
  // callers can route through `voidAsync` for error logging.
  assignTag?: (tagId: string, conversationUrn: string) => Promise<unknown>
  removeTagAssignment?: (tagId: string, conversationUrn: string) => Promise<unknown>
  addTag?: (name: string, color: string) => Promise<Tag | null | undefined>
  showTagPicker?: boolean
  onSetShowTagPicker?: (show: boolean) => void
  showReminderPicker?: boolean
  onSetShowReminderPicker?: (open: boolean) => void
  batchTagUrns?: Set<string> | null
  onClearBatchTag?: () => void
  copyRef?: React.MutableRefObject<(() => void) | null>
}

interface PendingMessage extends Message {
  pending: true
  clientId: string
}

export default function ThreadView({ conversationUrn, conversations, onMessageSent, onArchive, onRemind, onClearReminder, isReminded, allTags, getTagsForConversation, assignTag, removeTagAssignment, addTag, showTagPicker = false, onSetShowTagPicker, showReminderPicker = false, onSetShowReminderPicker, batchTagUrns, onClearBatchTag, copyRef }: ThreadViewProps) {
  const conversation = useConversation(conversationUrn)
  const isSalesNav = conversation?.category === 'SALESNAV'

  // --- CRM matching (Phase 2) ---
  const { connections: crmConnections } = useCrmConnections()
  const activeCrm = useActiveCrm(crmConnections)
  const targetProfileUrn =
    conversation && !conversation.isGroup && conversation.participantProfileUrn
      ? conversation.participantProfileUrn
      : null
  const { mapping: crmMapping, match: matchCrm, pickCandidate: pickCrmCandidate, skip: skipCrmMatch } =
    useCrmMapping(targetProfileUrn, activeCrm)
  const [matchInFlight, setMatchInFlight] = useState(false)

  // Track which (profileUrn, crmName) pairs we've already attempted a match for
  // in this session. useLiveQuery briefly returns `undefined` while Dexie re-runs
  // its query on any IDB write (new messages, read updates, heartbeats), which
  // would otherwise re-enter the match effect and flash the amber progress bar.
  const attemptedMatchRef = useRef<Set<string>>(new Set())

  // Trigger matching AFTER enrichment completes so we have the real name,
  // publicIdentifier, currentCompany, and can query Supabase for Harmonic
  // data (emails, company domain, experience history).
  //
  // Gate: primary._enrichedAt must be set. When the enrichment effect (below)
  // completes, IDB updates, conversation re-fires, and this effect proceeds.
  // For already-enriched contacts, fires immediately.
  //
  // Fallback: if enrichment never completes (failure/timeout), a 5-second
  // timer sets matchFallback to bypass the gate.
  const [matchFallback, setMatchFallback] = useState(false)
  const matchFallbackRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!activeCrm || !targetProfileUrn || !conversation) return
    if (crmConnections.affinity?.status !== 'active') return
    if (crmMapping) return  // already cached

    const primary = conversation.allParticipants?.[0]
    const enriched = !!primary?._enrichedAt

    // Start fallback timer on first evaluation if not yet enriched
    if (!enriched && !matchFallback) {
      if (!matchFallbackRef.current) {
        matchFallbackRef.current = setTimeout(() => setMatchFallback(true), 5000)
      }
      return  // wait for enrichment or fallback
    }

    const attemptKey = `${activeCrm}:${targetProfileUrn}`
    if (attemptedMatchRef.current.has(attemptKey)) return
    attemptedMatchRef.current.add(attemptKey)
    if (matchFallbackRef.current) { clearTimeout(matchFallbackRef.current); matchFallbackRef.current = null }

    setMatchInFlight(true)
    ;(async () => {
      try {
        // Gather Harmonic data from Supabase (may be null if not Harmonic-enriched)
        const harmonic = await fetchHarmonicMatchData(targetProfileUrn)

        const [firstName, ...rest] = (conversation.participantName || '').split(' ')
        await matchCrm({
          profileUrn: targetProfileUrn,
          firstName: primary?.currentTitle ? firstName : firstName || '',
          lastName: rest.join(' '),
          displayName: conversation.participantName,
          headline: conversation.participantHeadline,
          publicIdentifier: primary?.publicIdentifier || undefined,
          company: primary?.currentCompany || undefined,
          emails: harmonic?.emails,
          companyDomain: harmonic?.companyDomain || undefined,
          experience: harmonic?.experience,
        })
      } catch (e) {
        attemptedMatchRef.current.delete(attemptKey)
        console.warn('[CRM] Match failed for', targetProfileUrn, '—', (e as Error)?.message || e)
      } finally {
        setMatchInFlight(false)
      }
    })()
  }, [
    activeCrm,
    targetProfileUrn,
    conversation,
    crmConnections.affinity?.status,
    crmMapping,
    matchCrm,
    matchFallback,
  ])

  // Clean up fallback timer on conversation switch
  useEffect(() => {
    return () => {
      if (matchFallbackRef.current) { clearTimeout(matchFallbackRef.current); matchFallbackRef.current = null }
      setMatchFallback(false)
    }
  }, [conversationUrn])
  const [confirmedMessages, setConfirmedMessages] = useState<Message[]>([])
  const [pendingMessages, setPendingMessages] = useState<PendingMessage[]>([])
  const [replyTo, setReplyTo] = useState<Message | null>(null)
  const { toast, show: showToast } = useToast()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const fetchDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [noMoreMessages, setNoMoreMessages] = useState(false)

  // Map clientId → confirmed messageUrn for precise dedup
  const confirmedUrnMap = useRef(new Map<string, string>())
  // `stableKeyRef` used to live here — a ref mutated inside the
  // `messages` useMemo body (clear + set) to track the confirmed-urn →
  // original-clientId mapping for stable React keys. That's a classic
  // purity violation: useMemo bodies must be pure so React can
  // double-invoke them safely. The mapping is now returned AS PART OF
  // the memo result and consumed directly in render. No ref needed.
  // Mirror of pendingMessages for reads inside async timers — avoids reading
  // state via a functional setter callback (which must be pure). Kept in
  // sync by an effect below.
  const pendingMessagesRef = useRef<PendingMessage[]>([])
  // Track handleSent refresh timers so we can clear them on unmount or
  // conversation change. Without this the setTimeout keeps running after
  // navigation away and fires a sendCommand + setState against an
  // unmounted or stale-conversation component.
  const sentRefreshTimers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set())
  // Live mirror of the current conversationUrn. The handleSent callback
  // closes over `conversationUrn`, which means comparing
  // `capturedConversationUrn !== conversationUrn` inside a closure that
  // already captured the same value is a tautology. Reading the ref inside
  // the .then() gives us the *current* conversation at the moment the
  // REFRESH_THREAD response lands, not the one that was visible when the
  // message was sent.
  const conversationUrnRef = useRef(conversationUrn)
  useEffect(() => {
    conversationUrnRef.current = conversationUrn
  }, [conversationUrn])

  // Merge confirmed + pending, deduplicating matched pending messages.
  // Returns both the merged list AND the confirmed-urn → clientId
  // mapping so render can key off stable client ids even after a
  // pending → confirmed transition. Computing `stableKeys` fresh each
  // memo keeps the body pure (no ref mutation).
  const { messages, stableKeys } = useMemo<{
    messages: Message[]
    stableKeys: Map<string, string>
  }>(() => {
    const stableKeys = new Map<string, string>()
    if (pendingMessages.length === 0) {
      return { messages: confirmedMessages, stableKeys }
    }

    const stillPending = pendingMessages.filter((p) => {
      // Primary: match by confirmed URN mapping
      const realUrn = confirmedUrnMap.current.get(p.clientId)
      if (realUrn && confirmedMessages.some((m) => m.messageUrn === realUrn)) {
        stableKeys.set(realUrn, p.clientId)
        return false
      }

      // Fallback: text match (outgoing, same text, within 5 minutes)
      if (p.text) {
        const match = confirmedMessages.find(
          (real) =>
            real.isOutgoing &&
            real.text === p.text &&
            Math.abs(real.timestamp - p.timestamp) < 5 * 60 * 1000
        )
        if (match) {
          stableKeys.set(match.messageUrn, p.clientId)
          return false
        }
        return true
      }

      // Media-only: match by outgoing message with any attachment within 60s
      const match = confirmedMessages.find(
        (real) =>
          real.isOutgoing &&
          !real.text &&
          real.attachments?.length &&
          Math.abs(real.timestamp - p.timestamp) < 60 * 1000
      )
      if (match) {
        stableKeys.set(match.messageUrn, p.clientId)
        return false
      }
      return true
    })

    return {
      messages: [...confirmedMessages, ...stillPending].sort((a, b) => a.timestamp - b.timestamp),
      stableKeys,
    }
  }, [confirmedMessages, pendingMessages])

  // Read messages from IndexedDB directly (not useLiveQuery)
  const fetchMessagesFromDB = useCallback(async () => {
    if (!conversationUrn) return
    const msgs = await db.messages
      .where('conversationUrn')
      .equals(conversationUrn)
      .sortBy('timestamp')
    setConfirmedMessages(msgs)
  }, [conversationUrn])

  // Scroll position preservation (#18)
  const scrollPositions = useRef(new Map<string, number>())
  const prevUrnRef = useRef(conversationUrn)
  useEffect(() => {
    // Save scroll position of previous conversation before switching
    return () => {
      if (scrollContainerRef.current && prevUrnRef.current) {
        scrollPositions.current.set(prevUrnRef.current, scrollContainerRef.current.scrollTop)
      }
      prevUrnRef.current = conversationUrn
    }
  }, [conversationUrn])

  // Restore saved scroll position after messages load
  useEffect(() => {
    if (!messages.length || !conversationUrn) return
    const saved = scrollPositions.current.get(conversationUrn)
    if (saved != null) {
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = saved
      })
    }
  }, [conversationUrn, messages.length]) // re-run when message count changes

  // Keep the pendingMessages ref in sync so async timers can read the
  // current value without going through a functional setState callback.
  useEffect(() => {
    pendingMessagesRef.current = pendingMessages
  }, [pendingMessages])

  // Effect 1: Reset state + fetch messages when switching conversations
  useEffect(() => {
    setPendingMessages([])
    setReplyTo(null)
    setNoMoreMessages(false)
    // Eager clear: previously confirmedMessages retained the OLD thread's
    // messages until fetchMessagesFromDB resolved, causing a visible flash
    // of the prior conversation when rapidly switching. Clearing here
    // means the user sees an empty thread briefly — preferable to the
    // wrong thread's history.
    setConfirmedMessages([])
    // Also clear any in-flight handleSent refresh timers — they'd fire
    // REFRESH_THREAD against the *previous* conversation and splat its
    // result into the now-visible one.
    for (const t of sentRefreshTimers.current) clearTimeout(t)
    sentRefreshTimers.current.clear()

    if (!conversationUrn) return

    fetchMessagesFromDB()

    sendCommand('FETCH_MESSAGES', { conversationUrn }).catch((err) => console.warn('[ThreadView] FETCH_MESSAGES failed:', err?.message))
  }, [conversationUrn, fetchMessagesFromDB])

  // Clean up any outstanding refresh timers on unmount.
  useEffect(() => {
    const timers = sentRefreshTimers.current
    return () => {
      for (const t of timers) clearTimeout(t)
      timers.clear()
    }
  }, [])

  // Effect 2: Prefetch adjacent threads (can re-run when conversation list changes)
  useEffect(() => {
    if (conversationUrn) {
      prefetchAdjacent(conversations, conversationUrn)
    }
  }, [conversationUrn, conversations])

  // ACTIVE LISTENER: re-read messages from IndexedDB when the extension broadcasts updates.
  // The poll handles change detection and only fetches messages for changed conversations,
  // so by the time this event fires, the data is already in IndexedDB.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail
      if (detail?.type === 'messages') {
        if (detail.conversationUrns && !detail.conversationUrns.includes(conversationUrn)) return
        if (fetchDebounceRef.current) clearTimeout(fetchDebounceRef.current)
        fetchDebounceRef.current = setTimeout(() => fetchMessagesFromDB(), 50)
      }
    }
    window.addEventListener('fwdslash-data-updated', handler)
    return () => window.removeEventListener('fwdslash-data-updated', handler)
  }, [conversationUrn, fetchMessagesFromDB])

  // Callbacks for ComposeBar
  const handlePending = useCallback((msg: { clientId: string; text: string; previewUrl?: string }) => {
    const pending: PendingMessage = {
      messageUrn: msg.clientId,
      conversationUrn,
      senderName: '',
      senderProfileUrn: '',
      isOutgoing: true,
      text: msg.text,
      timestamp: Date.now(),
      attachments: msg.previewUrl
        ? [{ type: 'image', name: 'Image', url: msg.previewUrl }]
        : [],
      reactions: [],
      label: null,
      isSponsored: false,
      _cachedAt: Date.now(),
      pending: true,
      clientId: msg.clientId,
    }
    setPendingMessages((prev) => [...prev, pending])
  }, [conversationUrn])

  const handleSent = useCallback((info?: { clientId: string; confirmedUrn?: string }) => {
    // Store the confirmed URN mapping for dedup
    if (info?.confirmedUrn) {
      confirmedUrnMap.current.set(info.clientId, info.confirmedUrn)
    }

    // Notify Shell that a message was sent (clears draft from inbox list immediately)
    onMessageSent?.(conversationUrn)

    // For Sales Nav: skip REFRESH_THREAD — the extension broadcasts the message instantly
    // to IndexedDB and the fwdslash-data-updated event listener re-reads automatically.
    if (isSalesNav) return

    // Delayed fallback: only refresh if the pending message hasn't been
    // confirmed after 3s. Previously this read pendingMessages via a
    // `setPendingMessages((prev) => { sendCommand(...); return prev })`
    // callback — an impure reducer (side-effect during render-phase state
    // update). Under StrictMode React invokes updaters twice in dev,
    // firing sendCommand twice. Now we:
    //   1. Read the current pendingMessages from the ref (no setter abuse)
    //   2. Fire sendCommand as a plain side-effect
    //   3. Capture the scheduled timer so Effect 1 / unmount can clear it
    const pendingClientId = info?.clientId
    const capturedConversationUrn = conversationUrn
    const timer = setTimeout(() => {
      sentRefreshTimers.current.delete(timer)
      if (pendingClientId && confirmedUrnMap.current.has(pendingClientId)) return
      const stillHasPending = pendingMessagesRef.current.some((m) => m.clientId === pendingClientId)
      if (!stillHasPending) return
      sendCommand<{ messages: Message[] }>('REFRESH_THREAD', { conversationUrn: capturedConversationUrn })
        .then((result) => {
          // Guard: the user may have navigated; don't splat results
          // from the old conversation into whatever's visible now.
          // Compare against the ref (live value) not the closure-captured
          // `conversationUrn` — the latter was the same as
          // `capturedConversationUrn` the moment this callback was created,
          // making the old check a no-op.
          if (capturedConversationUrn !== conversationUrnRef.current) return
          if (result?.messages?.length) {
            setConfirmedMessages(result.messages.sort((a, b) => a.timestamp - b.timestamp))
          }
        })
        .catch((err) => console.warn('[ThreadView] REFRESH_THREAD fallback failed:', err?.message))
    }, 3000)
    sentRefreshTimers.current.add(timer)
  }, [conversationUrn, isSalesNav])

  const handleSendError = useCallback((msg: { clientId: string; text: string }, error: string) => {
    setPendingMessages((prev) => prev.filter((p) => p.clientId !== msg.clientId))
    showToast(error || 'Message failed to send')
  }, [showToast])

  // Optimistic reaction: instantly update the message's reactions in local state
  const handleOptimisticReact = useCallback((messageUrn: string, emoji: string) => {
    const toggleReaction = (msg: Message): Message => {
      if (msg.messageUrn !== messageUrn) return msg
      const existing = (msg.reactions || []).find((r) => r.emoji === emoji)
      if (existing?.viewerReacted) {
        const updated = msg.reactions
          .map((r) => r.emoji === emoji ? { ...r, count: r.count - 1, viewerReacted: false } : r)
          .filter((r) => r.count > 0)
        return { ...msg, reactions: updated }
      }
      if (existing) {
        return { ...msg, reactions: msg.reactions.map((r) => r.emoji === emoji ? { ...r, count: r.count + 1, viewerReacted: true } : r) }
      }
      return { ...msg, reactions: [...(msg.reactions || []), { emoji, count: 1, viewerReacted: true }] }
    }

    setConfirmedMessages((prev) => prev.map(toggleReaction))
    // Also update pending messages (#N4)
    setPendingMessages((prev) => prev.map((m) => toggleReaction(m) as PendingMessage))
  }, [])

  const handleCopyConversation = useCallback(async () => {
    if (!messages.length) return
    const text = formatConversationText(messages)
    await navigator.clipboard.writeText(text)
    showToast('Conversation copied')
  }, [messages, showToast])

  useEffect(() => {
    if (copyRef) copyRef.current = handleCopyConversation
    return () => { if (copyRef) copyRef.current = null }
  }, [handleCopyConversation, copyRef])

  // Optimistic edit: update message text in local state immediately
  const handleOptimisticEdit = useCallback((messageUrn: string, newText: string) => {
    setConfirmedMessages((prev) =>
      prev.map((msg) => msg.messageUrn === messageUrn ? { ...msg, text: newText } : msg)
    )
  }, [])



  // Enrich primary participant on first thread open.
  // Uses enrichAndSync which:
  //   1. Triggers the extension command (writes lean fields to IDB)
  //   2. Writes full enrichment data to Supabase
  // Guard: attemptedEnrichRef prevents retry loops when enrichment fails
  // (conversation updates from polling would otherwise re-trigger indefinitely).
  const attemptedEnrichRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!conversation || conversation.isGroup) return
    const primary = conversation.allParticipants?.[0]
    if (!primary?.profileUrn || primary._enrichedAt) return
    if (attemptedEnrichRef.current.has(primary.profileUrn)) return
    attemptedEnrichRef.current.add(primary.profileUrn)
    enrichAndSync(primary.profileUrn, conversationUrn, {
      name: primary.name,
      headline: primary.headline,
      profileImage: primary.profileImage,
      lastMessageTimestamp: conversation.lastMessageTimestamp,
    }).catch(() => {})
  }, [conversationUrn, conversation])

  // Silent backfill: only for threads with a full page of messages (likely more history).
  // Runs without any loading indicator — messages just appear at the top.
  // Capped at 20 iterations (~1000 messages) to prevent runaway API calls.
  useEffect(() => {
    if (!conversationUrn) return
    let cancelled = false
    const MAX_BACKFILL_ITERATIONS = 20
    const backfill = async () => {
      await new Promise((r) => setTimeout(r, 1500))

      // Check how many messages we have — skip backfill for short threads
      const count = await db.messages
        .where('conversationUrn')
        .equals(conversationUrn)
        .count()
      if (count < 20 || cancelled) return // < 20 = likely the entire thread

      let iterations = 0
      while (!cancelled && iterations < MAX_BACKFILL_ITERATIONS) {
        iterations++
        const oldest = await db.messages
          .where('conversationUrn')
          .equals(conversationUrn)
          .sortBy('timestamp')
          .then((msgs) => msgs[0])
        if (!oldest || cancelled) break
        try {
          const result = await sendCommand<{ messages: Message[] }>('FETCH_MESSAGES_BY_CURSOR', {
            conversationUrn,
            anchorTimestamp: oldest.timestamp,
            countBefore: 50,
            countAfter: 0,
            salesNav: isSalesNav || undefined,
          })
          if (!result?.messages?.length) {
            setNoMoreMessages(true)
            break
          }
          await fetchMessagesFromDB()
        } catch {
          break
        }
      }
    }
    backfill()
    return () => { cancelled = true }
  }, [conversationUrn, fetchMessagesFromDB, isSalesNav])

  // No JS scroll-to-bottom needed — column-reverse handles it natively.
  // The browser starts at scroll position 0 which is the BOTTOM in column-reverse.

  // Load older messages when user scrolls near the top.
  // In column-reverse, scrollTop is negative (or the distance from bottom varies by browser).
  // We detect "near top" by checking if the user has scrolled most of the way up.
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el || loadingOlder || noMoreMessages || !messages.length) return

    // In column-reverse, scrollTop is 0 at bottom and goes negative as you scroll up.
    // "Near top" = scrolled almost all the way up = Math.abs(scrollTop) close to max scroll.
    const maxScroll = el.scrollHeight - el.clientHeight
    const scrolledUp = Math.abs(el.scrollTop)
    const nearTop = scrolledUp >= maxScroll - 200

    if (nearTop) {
      const oldestTimestamp = messages[0].timestamp
      setLoadingOlder(true)

      sendCommand<{ messages: Message[] }>('FETCH_MESSAGES_BY_CURSOR', {
        conversationUrn,
        anchorTimestamp: oldestTimestamp,
        countBefore: 50,
        countAfter: 0,
        salesNav: isSalesNav || undefined,
      }).then(async (result) => {
        if (!result?.messages?.length) {
          setNoMoreMessages(true)
          setLoadingOlder(false)
        } else {
          await fetchMessagesFromDB()
          setLoadingOlder(false)
        }
      }).catch(() => {
        // Network error — don't mark as "no more", let user retry by scrolling again
        console.warn('[ThreadView] Failed to load older messages')
        setLoadingOlder(false)
      })
    }
  }, [conversationUrn, messages, loadingOlder, noMoreMessages, isSalesNav, fetchMessagesFromDB])

  if (!conversation) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-zinc-400">
        Loading...
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Thread header */}
      <header className="shrink-0 flex items-center gap-3 border-b border-zinc-100 px-6 py-3.5">
        {conversation.isGroup ? (
          <div className="flex shrink-0 -space-x-2">
            {(conversation.allParticipants || []).slice(0, 3).map((p, i) => {
              const k = p.profileUrn ?? `${p.name}-${i}`
              return p.profileImage ? (
                <img key={k} src={p.profileImage} alt="" className="h-8 w-8 rounded-full border-2 border-white" style={{ zIndex: 3 - i }} />
              ) : (
                <div key={k} className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-blue-100 text-[10px] font-semibold text-blue-600" style={{ zIndex: 3 - i }}>
                  {p.name.split(' ').map((w) => w[0]).join('').slice(0, 2)}
                </div>
              )
            })}
          </div>
        ) : conversation.participantProfileImage ? (
          <img src={conversation.participantProfileImage} alt="" className="h-9 w-9 rounded-full" />
        ) : (
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 text-[13px] font-semibold text-blue-600">
            {conversation.participantName.split(' ').map((p) => p[0]).join('').slice(0, 2)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h2 className="text-sm font-semibold text-zinc-900">
              {conversation.isGroup
                ? (conversation.allParticipants || []).map((p) => p.name.split(' ')[0]).join(', ')
                : conversation.participantName}
            </h2>
            {!conversation.isGroup && activeCrm && (
              <MappingDot mapping={crmMapping} crmName={activeCrm} />
            )}
            {isSalesNav && (
              <span className="shrink-0 rounded bg-blue-50 px-1.5 py-0.5 text-[9px] font-medium text-blue-600">Sales Nav</span>
            )}
          </div>
          {!conversation.isGroup && conversation.participantHeadline && (
            <p className="truncate text-xs text-zinc-400">{conversation.participantHeadline}</p>
          )}
          {!conversation.isGroup && (() => {
            const primary = conversation.allParticipants?.[0]
            if (!primary) return null
            const parts: string[] = []
            if (primary.currentTitle && primary.currentCompany) {
              parts.push(`${primary.currentTitle} at ${primary.currentCompany}`)
            } else if (primary.currentTitle || primary.currentCompany) {
              parts.push(primary.currentTitle || primary.currentCompany || '')
            }
            if (primary.location) parts.push(primary.location)
            if (primary.connectedAt) parts.push(`Connected ${formatConnectedDate(primary.connectedAt)}`)
            if (parts.length === 0 && !primary.publicIdentifier) return null
            return (
              <p className="truncate text-xs text-zinc-400">
                {parts.length > 0 && parts.join(' \u00b7 ')}
                {primary.publicIdentifier && (
                  <>
                    {parts.length > 0 && <span className="mx-1">{'\u00b7'}</span>}
                    <a
                      href={`https://www.linkedin.com/in/${primary.publicIdentifier}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline"
                    >
                      Profile
                    </a>
                  </>
                )}
              </p>
            )
          })()}
          {conversation.isGroup && (
            <p className="text-xs text-zinc-400">{(conversation.allParticipants || []).length} members</p>
          )}
          {/* Tags */}
          {allTags && getTagsForConversation && (
            <div className="mt-1 flex flex-wrap items-center gap-1">
              {getTagsForConversation(conversationUrn).map((tagId) => {
                const tag = allTags.find((t) => t.id === tagId)
                if (!tag) return null
                return <TagBadge key={tag.id} name={tag.name} color={tag.color} icon={tag.icon} shared={!!tag.team_id} onRemove={() => removeTagAssignment?.(tag.id, conversationUrn)} />
              })}
              <div className="relative">
                <button
                  onClick={() => onSetShowTagPicker?.(!showTagPicker)}
                  className="rounded px-1 py-0.5 text-[10px] text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
                >
                  + Tag
                </button>
                {showTagPicker && addTag && assignTag && removeTagAssignment && (() => {
                  const isBatch = batchTagUrns && batchTagUrns.size > 0
                  const targetUrns = isBatch ? [...batchTagUrns] : [conversationUrn]
                  // For batch: assignedTagIds = intersection (tags common to ALL selected)
                  const assignedTagIds = isBatch
                    ? (allTags || []).filter((t) => targetUrns.every((urn) => getTagsForConversation(urn).includes(t.id))).map((t) => t.id)
                    : getTagsForConversation(conversationUrn)
                  return (
                    <TagPicker
                      conversationUrn={conversationUrn}
                      tags={allTags}
                      assignedTagIds={assignedTagIds}
                      onAssign={(tagId) => { for (const urn of targetUrns) voidAsync(assignTag(tagId, urn), 'assignTag', { tagId, urn }) }}
                      onRemove={(tagId) => { for (const urn of targetUrns) voidAsync(removeTagAssignment(tagId, urn), 'removeTagAssignment', { tagId, urn }) }}
                      onCreateTag={addTag}
                      onClose={() => { onSetShowTagPicker?.(false); onClearBatchTag?.() }}
                    />
                  )
                })()}
              </div>
            </div>
          )}
        </div>
        {/* Conversation actions */}
        <div className="flex items-center gap-1">
          <HeaderAction
            title={conversation.isRead ? 'Mark unread' : 'Mark read'}
            shortcut={<Kbd>U</Kbd>}
            onClick={() => sendCommand(conversation.isRead ? 'MARK_UNREAD' : 'MARK_READ', { conversationUrn, salesNav: isSalesNav || undefined }).catch((err) => console.warn('[ThreadView] MARK_READ/UNREAD failed:', err?.message))}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
            </svg>
          </HeaderAction>
          <HeaderAction
            title={conversation.category === 'ARCHIVE' ? 'Unarchive' : 'Archive'}
            shortcut={<Kbd>E</Kbd>}
            onClick={() => onArchive?.(conversationUrn)}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" />
            </svg>
          </HeaderAction>
          <div className="relative">
            <HeaderAction
              title={isReminded ? 'Clear reminder' : 'Remind me'}
              shortcut={<Kbd>H</Kbd>}
              onClick={() => {
                if (isReminded) {
                  onClearReminder?.(conversationUrn)
                } else {
                  onSetShowReminderPicker?.(!showReminderPicker)
                }
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={isReminded ? '#8b5cf6' : 'currentColor'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </HeaderAction>
            {showReminderPicker && (
              <ReminderPicker
                onSelect={(date) => {
                  onSetShowReminderPicker?.(false)
                  onRemind?.(conversationUrn, date)
                }}
                onClose={() => onSetShowReminderPicker?.(false)}
              />
            )}
          </div>
          <HeaderAction
            title="Copy conversation"
            shortcut={<Kbd>⌘A</Kbd>}
            onClick={handleCopyConversation}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          </HeaderAction>
        </div>
      </header>

      {/* Messages */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="min-h-0 flex-1 flex-col-reverse overflow-y-auto px-6 py-4"
        style={{ display: 'flex', flexDirection: 'column-reverse' }}
      >
        {/* column-reverse: first child is at the BOTTOM of the scroll.
            We render messages in reverse so newest is first (at bottom). */}
        <div>
          {(!messages || messages.length === 0) && (
            <div className="py-8 text-center text-sm text-zinc-400">Loading messages...</div>
          )}
          {messages.map((msg, i) => {
            const flags = getGroupFlags(messages, i)
            const prev = i > 0 ? messages[i - 1] : null
            const showDateDivider = !prev || !isSameDay(prev.timestamp, msg.timestamp)
            return (
              <div key={stableKeys.get(msg.messageUrn) || msg.messageUrn}>
                {showDateDivider && <DateDivider timestamp={msg.timestamp} />}
                <MessageBubble
                  message={msg}
                  flags={flags}
                  isRecent={msg.isOutgoing && (Date.now() - msg.timestamp) < 60 * 60 * 1000}
                  isGroup={conversation?.isGroup || false}
                  allParticipants={conversation?.allParticipants || []}
                  onReply={() => setReplyTo(msg)}
                  onOptimisticReact={handleOptimisticReact}
                  onOptimisticEdit={handleOptimisticEdit}
                  showToast={showToast}
                  salesNav={isSalesNav}
                />
              </div>
            )
          })}
        </div>
        {loadingOlder && (
          <div className="py-2 text-center text-xs text-zinc-400">Loading older messages...</div>
        )}
      </div>

      {matchInFlight && !crmMapping && (
        <div className="h-0.5 w-full overflow-hidden bg-amber-100">
          <div className="h-full w-1/3 animate-pulse bg-amber-400" />
        </div>
      )}

      {crmMapping?.status === 'multiple_matches' && (
        <CrmMatchPicker
          mapping={crmMapping}
          onPick={async (id, name, email) => { await pickCrmCandidate(id, name, email) }}
          onSkip={async () => { await skipCrmMatch(conversation?.participantName) }}
        />
      )}

      <ComposeBar
        conversationUrn={conversationUrn}
        salesNav={conversation?.category === 'SALESNAV' || undefined}
        onPending={handlePending}
        onSent={handleSent}
        onSendError={handleSendError}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        onToast={(message) => showToast(message)}
      />

      {/* Toast notification */}
      {toast && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 rounded-lg bg-zinc-900 px-4 py-2 text-xs text-white shadow-lg">
          {toast}
        </div>
      )}
    </div>
  )
}

interface GroupFlags {
  isFirstInGroup: boolean
  isLastInGroup: boolean
  isMiddleInGroup: boolean
  isSingle: boolean
}

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🔥']

function MessageBubble({
  message,
  flags,
  isRecent,
  isGroup = false,
  allParticipants = [],
  onReply,
  onOptimisticReact,
  onOptimisticEdit,
  showToast,
  salesNav,
}: {
  message: Message | PendingMessage
  flags: GroupFlags
  isRecent: boolean
  isGroup?: boolean
  allParticipants?: { name: string; profileImage: string | null; profileUrn: string | null }[]
  onReply?: () => void
  onOptimisticReact?: (messageUrn: string, emoji: string) => void
  onOptimisticEdit?: (messageUrn: string, newText: string) => void
  showToast?: (msg: string) => void
  salesNav?: boolean
}) {
  const [hovered, setHovered] = useState(false)
  const [showReactions, setShowReactions] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editText, setEditText] = useState(message.text)
  const [recalled, setRecalled] = useState(false)
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const actionUrn = (message as unknown as { entityUrn?: string }).entityUrn || message.messageUrn
  const [pendingReactions, setPendingReactions] = useState(new Set<string>())

  const handleReact = (emoji: string) => {
    if (pendingReactions.has(emoji)) return // block rapid clicks
    const existing = (message.reactions || []).find((r) => r.emoji === emoji)
    const remove = existing?.viewerReacted === true
    setPendingReactions((prev) => new Set(prev).add(emoji))
    onOptimisticReact?.(message.messageUrn, emoji)
    sendCommand('REACT_MESSAGE', { messageUrn: actionUrn, emoji, remove, salesNav: salesNav || undefined })
      .catch(() => {
        onOptimisticReact?.(message.messageUrn, emoji) // revert optimistic toggle
        showToast?.('Reaction failed — try again')
      })
      .finally(() => setPendingReactions((prev) => { const next = new Set(prev); next.delete(emoji); return next }))
    setShowReactions(false)
  }

  const handleEdit = () => {
    if (!editText.trim() || editText === message.text) {
      setEditing(false)
      return
    }
    const newText = editText.trim()
    const oldText = message.text
    onOptimisticEdit?.(message.messageUrn, newText)
    setEditing(false)
    sendCommand('EDIT_MESSAGE', { messageUrn: actionUrn, text: newText, salesNav: salesNav || undefined })
      .then(() => sendCommand('REFRESH_THREAD', { conversationUrn: message.conversationUrn }))
      .catch(() => {
        onOptimisticEdit?.(message.messageUrn, oldText)
        showToast?.('Edit failed — try again')
      })
  }

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const handleRecall = () => {
    setShowDeleteConfirm(false)
    setRecalled(true)
    sendCommand('RECALL_MESSAGE', { messageUrn: actionUrn, salesNav: salesNav || undefined })
      .then(() => sendCommand('REFRESH_THREAD', { conversationUrn: message.conversationUrn }))
      .catch(() => {
        setRecalled(false)
        showToast?.('Delete failed — try again')
      })
  }

  // Spacing: first in group or single = normal gap, middle/last = tight
  const topMargin = flags.isFirstInGroup || flags.isSingle ? 'mt-3' : 'mt-1'

  // Bubble corner radius varies by position in group
  const outgoingRadius = flags.isSingle
    ? 'rounded-2xl'
    : flags.isFirstInGroup
    ? 'rounded-2xl rounded-br-md'
    : flags.isLastInGroup
    ? 'rounded-2xl rounded-tr-md'
    : 'rounded-2xl rounded-r-md'

  const incomingRadius = flags.isSingle
    ? 'rounded-2xl'
    : flags.isFirstInGroup
    ? 'rounded-2xl rounded-bl-md'
    : flags.isLastInGroup
    ? 'rounded-2xl rounded-tl-md'
    : 'rounded-2xl rounded-l-md'

  const showTimestamp = flags.isLastInGroup || flags.isSingle

  const isOut = message.isOutgoing


  // Show "deleted" state — either locally recalled or detected from LinkedIn (empty message)
  const isDeleted = recalled || (!message.text && message.attachments.length === 0 && !('pending' in message))
  if (isDeleted) {
    return (
      <div className={`${topMargin} ${isOut ? 'text-right' : 'text-left'}`}>
        <span className="inline-block rounded-2xl border border-zinc-200 px-3.5 py-2 text-[13px] italic text-zinc-400">
          This message has been deleted
        </span>
      </div>
    )
  }

  return (
    <>
    {/* Delete confirmation dialog */}
    {showDeleteConfirm && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20" onClick={() => setShowDeleteConfirm(false)}>
        <div className="w-[340px] rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
          <h3 className="text-lg font-semibold text-zinc-900">Delete message?</h3>
          <p className="mt-1 text-sm text-zinc-500">This can't be undone. The message will be removed for everyone.</p>
          <div className="mt-5 flex justify-end gap-2">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="rounded-lg border border-zinc-200 px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
            >
              Cancel
            </button>
            <button
              onClick={handleRecall}
              className="rounded-lg bg-zinc-900 px-4 py-2 text-sm font-medium text-white hover:bg-zinc-800"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    )}
    <div
      className={`group ${topMargin}`}
      onMouseEnter={() => {
        clearTimeout(leaveTimerRef.current)
        setHovered(true)
      }}
      onMouseLeave={() => {
        if (editing) return // Don't dismiss while editing
        leaveTimerRef.current = setTimeout(() => {
          setHovered(false)
          setShowReactions(false)
        }, 300)
      }}
    >
      {/* Sender name for group incoming messages (first in group only) */}
      {isGroup && !isOut && (flags.isFirstInGroup || flags.isSingle) && (
        <span className="mb-0.5 pl-1 text-[11px] font-medium text-zinc-500">
          {(() => {
            const sender = allParticipants.find((p) => p.profileUrn === message.senderProfileUrn)
            return sender?.name?.split(' ')[0] || message.senderName || 'Unknown'
          })()}
        </span>
      )}

      {/* Main row: single flex row, justify-start or justify-end */}
      <div className={`relative flex items-center gap-1.5 ${isOut ? 'flex-row-reverse' : 'flex-row'}`}>
        {/* Bubble */}
        {editing ? (
          <div className="flex w-[55%] flex-col gap-1.5">
            <textarea
              autoFocus
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleEdit() }
                if (e.key === 'Escape') setEditing(false)
              }}
              className="resize-none rounded-xl border border-blue-400 bg-white px-3.5 py-2 text-[13px] leading-relaxed text-zinc-900 focus:outline-none focus:ring-1 focus:ring-blue-400"
              rows={2}
            />
            <div className="flex gap-1.5 self-end">
              <button onClick={() => setEditing(false)} className="rounded-md px-2.5 py-1 text-[11px] text-zinc-500 hover:bg-zinc-100">Cancel</button>
              <button onClick={handleEdit} className="rounded-md bg-zinc-900 px-2.5 py-1 text-[11px] text-white hover:bg-zinc-800">Save</button>
            </div>
          </div>
        ) : isEmojiOnly(message) ? (
          /* Emoji-only: large emoji, no bubble */
          <span className="inline-block text-4xl leading-tight">{message.text}</span>
        ) : isMediaOnly(message) ? (
          /* Image/video/audio-only: no bubble background */
          <div className="inline-block max-w-[65%]">
            {message.attachments.map((a, i) => (
              <AttachmentRender key={a.url ?? `${a.type}-${i}`} attachment={a} isOutgoing={isOut} />
            ))}
          </div>
        ) : (
          <div
            className={`inline-block max-w-[65%] px-3.5 py-2 text-[13px] leading-relaxed ${
              isOut
                ? `${outgoingRadius} bg-zinc-900 text-zinc-50`
                : `${incomingRadius} bg-zinc-100 text-zinc-900`
            }`}
          >
            {message.text && <p className="whitespace-pre-wrap break-words">{renderMessageText(message.text, isOut, message.mentions)}</p>}
            {message.attachments.length > 0 && (
              <div className={message.text ? 'mt-1.5 space-y-1.5' : 'space-y-1.5'}>
                {message.attachments.map((a, i) => (
                  <AttachmentRender key={a.url ?? `${a.type}-${i}`} attachment={a} isOutgoing={isOut} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Reactions display */}
        {!editing && message.reactions?.length > 0 && (
          <div className={`flex gap-1 ${isOut ? 'flex-row-reverse' : 'flex-row'} mt-0.5`}>
            {message.reactions.map((r) => (
              <button
                key={r.emoji}
                onClick={() => handleReact(r.emoji)}
                className={`flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[11px] ${
                  r.viewerReacted
                    ? 'border-blue-200 bg-blue-50 text-blue-700'
                    : 'border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50'
                }`}
              >
                <span>{r.emoji}</span>
                {r.count > 1 && <span>{r.count}</span>}
              </button>
            ))}
          </div>
        )}

        {/* Inline actions — appear on the opposite side of the bubble */}
        {hovered && !editing && (
          <div className="flex items-center gap-0.5">
            <InlineAction onClick={() => setShowReactions(!showReactions)}><SmileyIcon /></InlineAction>
            <InlineAction onClick={onReply}><ReplyIcon /></InlineAction>
            {isOut && isRecent && (
              <>
                {isTextOnly(message) && (
                  <InlineAction onClick={() => { setEditing(true); setEditText(message.text) }}><EditIcon /></InlineAction>
                )}
                <InlineAction onClick={() => setShowDeleteConfirm(true)}><TrashIcon /></InlineAction>
              </>
            )}
          </div>
        )}

        {/* Reaction picker */}
        {showReactions && (
          <div className={`absolute ${isOut ? 'right-0' : 'left-0'} bottom-full mb-1 flex gap-0.5 rounded-full border border-zinc-200 bg-white px-1.5 py-1 shadow-lg`}>
            {QUICK_REACTIONS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => handleReact(emoji)}
                className="flex h-7 w-7 items-center justify-center rounded-full text-base hover:bg-zinc-100"
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Timestamp — below, aligned to the bubble side */}
      {showTimestamp && !editing && (
        <div className={`${isOut ? 'text-right' : 'text-left'}`}>
          <span className="mt-0.5 inline-block px-1 text-[10px] text-zinc-300">
            {new Date(message.timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            {'pending' in message && message.pending && <span className="ml-1">Sending...</span>}
          </span>
        </div>
      )}
    </div>
    </>
  )
}

function MappingDot({ mapping, crmName }: { mapping: CrmMapping | undefined; crmName: CrmName | null }) {
  if (!crmName) return null
  if (!mapping) {
    return <span title="Checking Affinity..." className="inline-block h-1.5 w-1.5 rounded-full bg-zinc-300" />
  }
  if (mapping.status === 'matched' || mapping.status === 'created') {
    return <span title={`Paired with ${mapping.crmContactName} in Affinity`} className="inline-block h-1.5 w-1.5 rounded-full bg-green-500" />
  }
  if (mapping.status === 'multiple_matches') {
    return <span title="Multiple Affinity matches — pick one below" className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
  }
  if (mapping.status === 'manual_skip') {
    return <span title="Sync skipped for this contact" className="inline-block h-1.5 w-1.5 rounded-full bg-zinc-400" />
  }
  return <span title="Not in Affinity yet" className="inline-block h-1.5 w-1.5 rounded-full bg-zinc-300" />
}

function HeaderAction({ children, title, shortcut, onClick }: { children: React.ReactNode; title: string; shortcut?: React.ReactNode; onClick?: () => void }) {
  return (
    <ActionTooltip label={title} shortcut={shortcut}>
      <button
        onClick={onClick}
        className="flex h-8 w-8 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
      >
        {children}
      </button>
    </ActionTooltip>
  )
}

function InlineAction({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex h-6 w-6 items-center justify-center rounded-full opacity-40 transition-opacity hover:opacity-100"
    >
      {children}
    </button>
  )
}

function DateDivider({ timestamp }: { timestamp: number }) {
  return (
    <div className="flex items-center gap-4 py-4">
      <div className="h-px flex-1 bg-zinc-100" />
      <span className="text-[10px] font-medium tracking-wider text-zinc-400">
        {formatDateLabel(timestamp)}
      </span>
      <div className="h-px flex-1 bg-zinc-100" />
    </div>
  )
}

function isSameDay(ts1: number, ts2: number): boolean {
  const a = new Date(ts1)
  const b = new Date(ts2)
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

const DAY_NAMES = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY']
const MONTH_NAMES = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']

function formatDateLabel(ts: number): string {
  const date = new Date(ts)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const msgDay = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const diffDays = Math.floor((today.getTime() - msgDay.getTime()) / 86_400_000)

  if (diffDays === 0) return 'TODAY'
  if (diffDays === 1) return 'YESTERDAY'
  if (diffDays < 7) return DAY_NAMES[date.getDay()]

  const month = MONTH_NAMES[date.getMonth()]
  const day = date.getDate()

  if (date.getFullYear() !== now.getFullYear()) {
    return `${month} ${day}, ${date.getFullYear()}`
  }
  return `${month} ${day}`
}

function formatConnectedDate(ts: number): string {
  const date = new Date(ts)
  const month = MONTH_NAMES[date.getMonth()]
  return `${month} ${date.getFullYear()}`
}

function SmileyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a1a1aa" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" />
    </svg>
  )
}

function ReplyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a1a1aa" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 17 4 12 9 7" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" />
    </svg>
  )
}

function EditIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#a1a1aa" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  )
}

function TrashIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  )
}

// Message is image/GIF only — no text, just visual media
function isTextOnly(message: Message): boolean {
  const richTypes = new Set(['audio', 'video', 'file'])
  return !message.attachments.some((a) => richTypes.has(a.type))
}

// Messages that render without a bubble: images, video, audio (with URLs)
const BUBBLE_FREE_TYPES = new Set(['image', 'video', 'audio', 'file'])

function isMediaOnly(message: Message): boolean {
  if (message.text && message.text.trim()) return false
  if (message.attachments.length === 0) return false
  return message.attachments.every((a) => BUBBLE_FREE_TYPES.has(a.type) && a.url)
}

// Emoji-only: text is 1-3 emoji characters with no other content
const EMOJI_REGEX = /^[\p{Emoji_Presentation}\p{Extended_Pictographic}\uFE0F\u200D]{1,6}$/u

function isEmojiOnly(message: Message): boolean {
  if (message.attachments.length > 0) return false
  const text = message.text?.trim()
  if (!text) return false
  return EMOJI_REGEX.test(text)
}

const GROUP_GAP_MS = 60 * 60 * 1000 // 60 minutes — generous grouping like LinkedIn
const RICH_ATTACHMENT_TYPES = new Set(['audio', 'video'])

function canGroup(a: Message, b: Message): boolean {
  if (a.senderProfileUrn !== b.senderProfileUrn) return false
  if (Math.abs(b.timestamp - a.timestamp) > GROUP_GAP_MS) return false
  // Don't group if either has a rich attachment
  const aHasRich = a.attachments.some((att) => RICH_ATTACHMENT_TYPES.has(att.type))
  const bHasRich = b.attachments.some((att) => RICH_ATTACHMENT_TYPES.has(att.type))
  if (aHasRich || bHasRich) return false
  return true
}

function getGroupFlags(messages: Message[], index: number): GroupFlags {
  const prev = index > 0 ? messages[index - 1] : null
  const next = index < messages.length - 1 ? messages[index + 1] : null
  const msg = messages[index]

  const groupedWithPrev = prev ? canGroup(prev, msg) : false
  const groupedWithNext = next ? canGroup(msg, next) : false

  if (!groupedWithPrev && !groupedWithNext) {
    return { isSingle: true, isFirstInGroup: false, isLastInGroup: false, isMiddleInGroup: false }
  }
  if (!groupedWithPrev && groupedWithNext) {
    return { isSingle: false, isFirstInGroup: true, isLastInGroup: false, isMiddleInGroup: false }
  }
  if (groupedWithPrev && !groupedWithNext) {
    return { isSingle: false, isFirstInGroup: false, isLastInGroup: true, isMiddleInGroup: false }
  }
  return { isSingle: false, isFirstInGroup: false, isLastInGroup: false, isMiddleInGroup: true }
}

function AttachmentRender({ attachment, isOutgoing }: {
  attachment: { type: string; name?: string; url?: string; size?: number; title?: string; description?: string; thumbnail?: string }
  isOutgoing: boolean
}) {
  const url = safeExternalUrl(attachment.url)
  const thumbnailUrl = safeExternalUrl(attachment.thumbnail)
  const textColor = isOutgoing ? 'text-zinc-300' : 'text-zinc-500'

  switch (attachment.type) {
    case 'image':
      return url ? (
        <a href={url} target="_blank" rel="noopener noreferrer">
          <img
            src={url}
            alt={attachment.name || 'Image'}
            className="max-w-[300px] rounded-2xl"
            loading="lazy"
            onError={(e) => {
              // Hide the wrapping anchor so we don't leave a zero-size clickable
              // region when LinkedIn's CDN 403s (signed-URL expiry).
              const img = e.currentTarget
              const anchor = img.closest('a')
              if (anchor instanceof HTMLElement) anchor.style.display = 'none'
              else img.style.display = 'none'
            }}
          />
        </a>
      ) : (
        <div className={`flex items-center gap-1.5 text-[11px] ${textColor}`}>
          <span>🖼️</span> <span>{attachment.name || 'Image'}</span>
        </div>
      )

    case 'video':
      return url ? (
        <video controls className="max-w-[280px] rounded-lg" src={url} />
      ) : (
        <div className={`flex items-center gap-1.5 text-[11px] ${textColor}`}>
          <span>🎬</span> <span>{attachment.name || 'Video'}</span>
        </div>
      )

    case 'audio':
      return url ? (
        <audio controls className="max-w-[260px]" src={url} />
      ) : (
        <div className={`flex items-center gap-1.5 text-[11px] ${textColor}`}>
          <span>🎵</span> <span>{attachment.name || 'Audio'}</span>
        </div>
      )

    case 'file':
      return <FileAttachment attachment={attachment} isOutgoing={isOutgoing} />

    case 'link': {
      const className = `block max-w-[300px] overflow-hidden rounded-xl border ${
        isOutgoing ? 'border-zinc-700' : 'border-zinc-200'
      }`
      const card = (
        <>
          {thumbnailUrl && (
            <img
              src={thumbnailUrl}
              alt=""
              className="h-36 w-full object-cover"
              loading="lazy"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
            />
          )}
          <div className={`px-3 py-2.5 ${isOutgoing ? 'bg-zinc-800' : 'bg-zinc-50'}`}>
            {attachment.title && (
              <p className={`text-[12px] font-medium leading-snug ${
                isOutgoing ? 'text-zinc-100' : 'text-zinc-900'
              }`}>
                {attachment.title}
              </p>
            )}
            {attachment.description && (
              <p className={`mt-0.5 line-clamp-2 text-[11px] leading-snug ${
                isOutgoing ? 'text-zinc-400' : 'text-zinc-500'
              }`}>
                {attachment.description}
              </p>
            )}
            {!attachment.title && !attachment.description && (
              <p className={`text-[11px] ${isOutgoing ? 'text-zinc-300' : 'text-zinc-500'}`}>Shared content</p>
            )}
          </div>
        </>
      )

      return url ? (
        <a href={url} target="_blank" rel="noopener noreferrer" className={className}>
          {card}
        </a>
      ) : (
        <div className={className}>
          {card}
        </div>
      )
    }

    default:
      return (
        <div className={`flex items-center gap-1.5 text-[11px] ${textColor}`}>
          <span>📄</span> <span>{attachment.name || attachment.type}</span>
        </div>
      )
  }
}

const FILE_EXT_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  pdf:  { label: 'PDF',  color: 'text-red-600',    bg: 'bg-red-50' },
  doc:  { label: 'DOC',  color: 'text-blue-600',   bg: 'bg-blue-50' },
  docx: { label: 'DOC',  color: 'text-blue-600',   bg: 'bg-blue-50' },
  xls:  { label: 'XLS',  color: 'text-green-600',  bg: 'bg-green-50' },
  xlsx: { label: 'XLS',  color: 'text-green-600',  bg: 'bg-green-50' },
  csv:  { label: 'CSV',  color: 'text-green-600',  bg: 'bg-green-50' },
  ppt:  { label: 'PPT',  color: 'text-orange-600', bg: 'bg-orange-50' },
  pptx: { label: 'PPT',  color: 'text-orange-600', bg: 'bg-orange-50' },
  txt:  { label: 'TXT',  color: 'text-zinc-600',   bg: 'bg-zinc-100' },
  zip:  { label: 'ZIP',  color: 'text-amber-600',  bg: 'bg-amber-50' },
  rar:  { label: 'RAR',  color: 'text-amber-600',  bg: 'bg-amber-50' },
}

function FileAttachment({ attachment }: {
  attachment: { name?: string; url?: string; size?: number }
  isOutgoing: boolean
}) {
  const name = attachment.name || 'File'
  const ext = name.split('.').pop()?.toLowerCase() || ''
  const config = FILE_EXT_CONFIG[ext] || { label: ext.toUpperCase().slice(0, 4) || 'FILE', color: 'text-zinc-500', bg: 'bg-zinc-100' }

  const inner = (
    <div className="flex max-w-[280px] items-center gap-3 rounded-xl border border-zinc-200 bg-white px-3.5 py-3">
      {/* File type badge */}
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${config.bg}`}>
        <span className={`text-[10px] font-bold tracking-wide ${config.color}`}>{config.label}</span>
      </div>
      {/* File info */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[12px] font-medium leading-snug text-zinc-900">
          {name}
        </p>
        <p className="mt-0.5 text-[10px] text-zinc-500">
          {attachment.size ? formatBytes(attachment.size) : config.label}
          {attachment.url && <span> · Tap to open</span>}
        </p>
      </div>
      {/* Download arrow */}
      {attachment.url && (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#71717a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="7 10 12 15 17 10" />
          <line x1="12" y1="15" x2="12" y2="3" />
        </svg>
      )}
    </div>
  )

  const url = safeExternalUrl(attachment.url)
  return url ? (
    <a href={url} target="_blank" rel="noopener noreferrer" className="block">
      {inner}
    </a>
  ) : inner
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const URL_REGEX = /https?:\/\/[^\s<>'")\]]+/g

// Validate a profileUrn destined for an <a href> in the linkedin.com/in/<id>
// path. LinkedIn's public identifiers / profile URNs are always a restricted
// alphabet (letters, digits, underscore, dash), sometimes prefixed with
// `urn:li:fsd_profile:`. Anything else in this field is upstream corruption
// or a crafted payload trying to break out of the URL — e.g. `foo"` to close
// the href attribute, or `foo/../../evil` to rewrite the path. Returning
// null tells the renderer to emit plain text instead of a link so a bad
// value is inert.
function sanitizeProfileUrnForHref(raw: string | undefined | null): string | null {
  if (!raw || typeof raw !== 'string') return null
  const bare = raw.startsWith('urn:li:fsd_profile:')
    ? raw.slice('urn:li:fsd_profile:'.length)
    : raw
  if (!/^[A-Za-z0-9_-]{1,128}$/.test(bare)) return null
  return bare
}

function renderMessageText(
  text: string,
  isOutgoing: boolean,
  mentions?: { start: number; length: number; profileUrn: string }[]
): (string | JSX.Element)[] {
  // Build sorted list of ranges: mentions + URLs
  type Range = { start: number; end: number; type: 'url' | 'mention'; value: string; profileUrn?: string }
  const ranges: Range[] = []

  // Add mention ranges
  if (mentions?.length) {
    for (const m of mentions) {
      ranges.push({ start: m.start, end: m.start + m.length, type: 'mention', value: text.slice(m.start, m.start + m.length), profileUrn: m.profileUrn })
    }
  }

  // Add URL ranges
  URL_REGEX.lastIndex = 0
  let urlMatch: RegExpExecArray | null
  while ((urlMatch = URL_REGEX.exec(text)) !== null) {
    const urlStart = urlMatch.index
    const urlEnd = urlStart + urlMatch[0].length
    // Skip if overlapping with a mention
    if (!ranges.some((r) => urlStart < r.end && urlEnd > r.start)) {
      ranges.push({ start: urlStart, end: urlEnd, type: 'url', value: urlMatch[0] })
    }
  }

  ranges.sort((a, b) => a.start - b.start)

  if (ranges.length === 0) return [text]

  const parts: (string | JSX.Element)[] = []
  let lastIndex = 0

  for (const range of ranges) {
    if (range.start > lastIndex) {
      parts.push(text.slice(lastIndex, range.start))
    }
    if (range.type === 'url') {
      parts.push(
        <a
          key={`url-${range.start}`}
          href={safeExternalUrl(range.value, { allowedProtocols: ['https:', 'http:'] }) ?? '#'}
          target="_blank"
          rel="noopener noreferrer"
          className={`underline ${isOutgoing ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-700'}`}
        >
          {range.value}
        </a>
      )
    } else {
      // Only render as a link if the profileUrn is well-formed. A bad value
      // (corrupt upstream or crafted to break out of href="") falls back to
      // plain text — still reads as a mention, just not clickable.
      const safeId = sanitizeProfileUrnForHref(range.profileUrn)
      if (safeId) {
        parts.push(
          <a
            key={`mention-${range.start}`}
            href={`https://www.linkedin.com/in/${encodeURIComponent(safeId)}`}
            target="_blank"
            rel="noopener noreferrer"
            className={`font-medium no-underline ${isOutgoing ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-700'}`}
          >
            {range.value}
          </a>
        )
      } else {
        parts.push(
          <span key={`mention-${range.start}`} className="font-medium">{range.value}</span>
        )
      }
    }
    lastIndex = range.end
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts
}
